"use client"

import * as React from "react"
import { DayPicker } from "react-day-picker"
import { getDefaultClassNames } from 'react-day-picker'

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button-variants"

export type CalendarSingleValue = Date | undefined;
export type CalendarProps = Omit<React.ComponentProps<typeof DayPicker>, 'mode' | 'selected' | 'defaultMonth' | 'onSelect'> & {
  /** only support single date selection mode by default */
  mode?: 'single';
  /** selected date */
  selected?: CalendarSingleValue;
  /** default month to display */
  defaultMonth?: CalendarSingleValue;
  /** called when a date is selected */
  onSelect?: (date: CalendarSingleValue) => void;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  mode = 'single',
  selected,
  defaultMonth,
  onSelect,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      mode={mode}
      selected={selected}
      defaultMonth={defaultMonth}
      onSelect={(date) => onSelect?.(date)}
      showOutsideDays={showOutsideDays}
      className={cn("p-2", className)}
      classNames={{
        ...getDefaultClassNames(),
        // Caption
        // month_caption: "text-sm flex items-center justify-center",
        caption_label: "text-base flex items-center justify-center",
        // Much lighter navigation chevrons
        chevron: 'h-3 w-3 text-gray-500',
        // Lighter, smaller weekdays labels
        weekday: 'text-muted-foreground w-7 font-normal text-xs pb-1',
        // Mute days outside the current month
        outside: 'text-muted-foreground opacity-50 text-sm',
        // Remove today highlighting
        today: 'text-foreground font-normal',
        // More compact day buttons
        day: "h-7 w-7 p-1 font-normal text-sm hover:bg-accent hover:text-accent-foreground rounded-md",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-7 w-7 p-1 font-normal text-sm hover:bg-accent hover:text-accent-foreground rounded-md"
        ),
        // Clean navigation buttons (no borders)
        nav_button_previous: cn(
          buttonVariants({ variant: "ghost" }),
          "h-6 w-6 bg-transparent p-0 opacity-70 hover:opacity-100 border-0"
        ),
        nav_button_next: cn(
          buttonVariants({ variant: "ghost" }),
          "h-6 w-6 bg-transparent p-0 opacity-70 hover:opacity-100 border-0"
        ),
        // Selected date styling
        selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md",
        // Much tighter table spacing
        table: "w-full border-collapse",
        tbody: "",
        row: "flex w-full",
        cell: "text-center text-sm relative p-0 focus-within:relative focus-within:z-20",
        ...classNames,
      }}
      {...props}
    />
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
